import React, { useState, useEffect } from 'react';
import { api, apiClient } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Download,
  Building2,
  Users,
  ArrowRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const REQUIRED_FIELDS = ['first_name', 'last_name', 'email'];
const TARGET_FIELDS = [
  'first_name','last_name','full_name','email','phone','date_of_birth','gender','tobacco_user','address','city','state','state_of_residence','zip_code','job_title','department','hire_date','annual_salary','pay_frequency','filing_status','dependents','w4_allowances','w4_version','w4_step2_multiple_jobs','w4_step3_total','w4_step4c_extra_withholding','employment_status','flex_allocation','post_tax_deduction'
];

const normalizeHeader = (h) => h?.toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const buildDefaultMapping = (headers) => {
  const mapping = {};
  headers.forEach((h) => {
    const normalized = normalizeHeader(h);
    if (TARGET_FIELDS.includes(normalized)) {
      mapping[h] = normalized;
    } else if (normalized === 'name') {
      mapping[h] = 'full_name';
    } else if (normalized === 'nw4') {
      mapping[h] = 'w4_version';
    } else if (normalized === 's_m_h' || normalized === 'smh') {
      mapping[h] = 'filing_status';
    } else if (normalized.includes('exemption')) {
      // Matches "Exemptions", "2019 Exemptions", "Exemptions (2019)", etc. — a
      // plain equality check missed any header with a year prefix/suffix,
      // silently dropping pre-2020 W4 allowances/dependents from the census.
      mapping[h] = 'w4_allowances';
    } else if (normalized === 'step_2_box_y_n' || normalized === 'step2_box' || normalized === 'step2') {
      mapping[h] = 'w4_step2_multiple_jobs';
    } else if (normalized === 'step_3_total' || normalized === 'step3_total' || normalized === 'step3') {
      mapping[h] = 'w4_step3_total';
    } else if (normalized === 'pre_deduct' || normalized === 'pre_deducted' || normalized === 'pre_deduction') {
      mapping[h] = 'flex_allocation';
    } else if (normalized === 'post_deduct' || normalized === 'post_deducted' || normalized === 'post_deduction') {
      mapping[h] = 'post_tax_deduction';
    } else if (normalized === 'extra_w2' || normalized === 'extra_w4' || normalized === 'extra_withholding') {
      mapping[h] = 'w4_step4c_extra_withholding';
    } else if (normalized === 'salary') {
      mapping[h] = 'annual_salary';
    } else {
      mapping[h] = '';
    }
  });
  return mapping;
};

const normalizeYesNo = (value) => {
  if (value === undefined || value === null) return '';
  const normalized = value.toString().trim().toLowerCase();
  if (['y', 'yes', 'true', '1'].includes(normalized)) return true;
  if (['n', 'no', 'false', '0'].includes(normalized)) return false;
  return '';
};

const normalizeFilingStatus = (value) => {
  if (!value) return '';
  const normalized = value.toString().trim().toLowerCase();
  if (normalized.startsWith('m')) return 'married';
  if (normalized.startsWith('s')) return 'single';
  if (normalized.startsWith('h')) return 'single';
  return normalized;
};

const normalizeW4Version = (value) => {
  const yn = normalizeYesNo(value);
  if (yn === true) return 'post_2020';
  if (yn === false) return 'pre_2020';
  return '';
};

const toNumberOrEmpty = (value) => {
  if (value === undefined || value === null || value === '') return '';
  const num = Number(value);
  return Number.isFinite(num) ? num : '';
};

const splitFullName = (value) => {
  if (!value) return { firstName: '', lastName: '' };
  const parts = value.toString().trim().split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
};

const applyMapping = (data, mapping, defaults = {}) => {
  return data.map((row) => {
    const mapped = {};
    Object.entries(mapping).forEach(([source, target]) => {
      if (!target) return;
      const value = row[source];
      mapped[target] = value ?? defaults[target] ?? '';
    });

    if (mapped.full_name && (!mapped.first_name || !mapped.last_name)) {
      const { firstName, lastName } = splitFullName(mapped.full_name);
      if (!mapped.first_name) mapped.first_name = firstName;
      if (!mapped.last_name) mapped.last_name = lastName;
    }

    if (mapped.w4_version) {
      mapped.w4_version = normalizeW4Version(mapped.w4_version) || mapped.w4_version;
    }

    if (mapped.w4_step2_multiple_jobs !== undefined) {
      const normalized = normalizeYesNo(mapped.w4_step2_multiple_jobs);
      mapped.w4_step2_multiple_jobs = normalized === '' ? mapped.w4_step2_multiple_jobs : normalized;
    }

    if (mapped.filing_status) {
      mapped.filing_status = normalizeFilingStatus(mapped.filing_status);
    }

    if (mapped.w4_allowances !== undefined) {
      mapped.w4_allowances = toNumberOrEmpty(mapped.w4_allowances);
    }

    if ((mapped.dependents === undefined || mapped.dependents === '') && mapped.w4_allowances !== undefined && mapped.w4_allowances !== '') {
      mapped.dependents = mapped.w4_allowances;
    }

    if (mapped.w4_step3_total !== undefined) {
      mapped.w4_step3_total = toNumberOrEmpty(mapped.w4_step3_total);
    }

    if (mapped.w4_step4c_extra_withholding !== undefined) {
      mapped.w4_step4c_extra_withholding = toNumberOrEmpty(mapped.w4_step4c_extra_withholding);
    }

    if (mapped.annual_salary !== undefined) {
      mapped.annual_salary = toNumberOrEmpty(mapped.annual_salary);
    }

    if (mapped.flex_allocation !== undefined) {
      mapped.flex_allocation = toNumberOrEmpty(mapped.flex_allocation);
    }

    if (mapped.post_tax_deduction !== undefined) {
      mapped.post_tax_deduction = toNumberOrEmpty(mapped.post_tax_deduction);
    }

    // Apply defaults for any unmapped targets explicitly provided
    Object.entries(defaults).forEach(([target, value]) => {
      if (mapped[target] === undefined || mapped[target] === null || mapped[target] === '') {
        mapped[target] = value;
      }
    });

    // Final type coercion (runs after defaults too, since a manually-entered
    // default is always a raw string): Employee.dependents/w4_allowances are
    // Int columns and Employee.tobacco_user is a Boolean column in Prisma, so
    // sending the raw CSV/Excel string values as-is fails employee creation.
    if (mapped.dependents !== undefined) {
      mapped.dependents = toNumberOrEmpty(mapped.dependents) || 0;
    }
    if (mapped.w4_allowances !== undefined) {
      mapped.w4_allowances = toNumberOrEmpty(mapped.w4_allowances) || 0;
    }
    if (mapped.tobacco_user !== undefined) {
      const normalized = normalizeYesNo(mapped.tobacco_user);
      mapped.tobacco_user = normalized === '' ? false : normalized;
    }

    return mapped;
  });
};

const validateRows = (rows) => {
  const errors = [];
  rows.forEach((row, idx) => {
    REQUIRED_FIELDS.forEach((field) => {
      if (!row[field] || row[field].toString().trim() === '') {
        errors.push(`Row ${idx + 1}: Missing ${field}`);
      }
    });
  });
  return errors;
};

const fillTemplate = (template, tokens) => {
  let output = template;
  Object.entries(tokens).forEach(([key, value]) => {
    const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
    output = output.replace(regex, value ?? '');
  });
  return output;
};

export default function CensusUpload() {
  const [employers, setEmployers] = useState([]);
  const [selectedEmployer, setSelectedEmployer] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [columnHeaders, setColumnHeaders] = useState([]);
  const [fieldMapping, setFieldMapping] = useState({});
  const [mappingConfirmed, setMappingConfirmed] = useState(false);
  const [mappedData, setMappedData] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [sendInvites, setSendInvites] = useState(true);
  const [sendReminders, setSendReminders] = useState(true);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [defaultValues, setDefaultValues] = useState({});
  const [defaultField, setDefaultField] = useState('');
  const [defaultValueInput, setDefaultValueInput] = useState('');
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState(null);

  const mappedPreviewData = mappedData || (extractedData ? applyMapping(extractedData, fieldMapping, defaultValues) : null);
  const totalPreview = mappedPreviewData?.length || 0;
  const safePreviewIndex = Math.min(previewIndex, Math.max(totalPreview - 1, 0));
  const currentPreview = mappedPreviewData ? mappedPreviewData[safePreviewIndex] : null;

  useEffect(() => {
    loadEmployers();
  }, []);

  const loadEmployers = async () => {
    try {
      const data = await api.entities.Employer.list();
      setEmployers(data);

      // Tenant-scoped users will only see one employer; preselect it.
      if (data?.length === 1) {
        setSelectedEmployer(data[0].id);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setExtractedData(null);
      setColumnHeaders([]);
      setFieldMapping({});
      setMappedData(null);
      setMappingConfirmed(false);
      setValidationErrors([]);
      setImportResults(null);
    }
  };

  const handleUploadAndExtract = async () => {
    if (!file || !selectedEmployer) {
      toast.error('Please select an employer and upload a file');
      return;
    }

    setUploading(true);
    try {
      // Upload file
      const { file_url } = await api.integrations.Core.UploadFile({ file });
      
      setUploading(false);
      setProcessing(true);

      // Extract data from file
      const result = await api.integrations.Core.ExtractDataFromUploadedFile({
        file_url,
        json_schema: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              name: { type: 'string' },
              nw4: { type: 'string' },
              s_m_h: { type: 'string' },
              exemptions: { type: 'number' },
              step_2_box_y_n: { type: 'string' },
              step_3_total: { type: 'number' },
              pre_deduct: { type: 'number' },
              post_deduct: { type: 'number' },
              extra_w2: { type: 'number' },
              state_tax: { type: 'number' },
              first_name: { type: 'string' },
              last_name: { type: 'string' },
              email: { type: 'string' },
              phone: { type: 'string' },
              date_of_birth: { type: 'string' },
              gender: { type: 'string' },
              address: { type: 'string' },
              city: { type: 'string' },
              state: { type: 'string' },
              state_of_residence: { type: 'string' },
              zip_code: { type: 'string' },
              job_title: { type: 'string' },
              department: { type: 'string' },
              hire_date: { type: 'string' },
              annual_salary: { type: 'number' },
              pay_frequency: { type: 'string' },
              filing_status: { type: 'string' },
              dependents: { type: 'number' },
              w4_allowances: { type: 'number' },
              w4_version: { type: 'string' },
              w4_step2_multiple_jobs: { type: 'boolean' },
              w4_step3_total: { type: 'number' },
              w4_step4c_extra_withholding: { type: 'number' },
              tobacco_user: { type: 'boolean' },
              employment_status: { type: 'string' },
              flex_allocation: { type: 'number' },
              post_tax_deduction: { type: 'number' }
            }
          }
        }
      });

      if (result.status === 'success' && result.output) {
        setExtractedData(Array.isArray(result.output) ? result.output : [result.output]);
        const headers = Object.keys(Array.isArray(result.output) ? result.output[0] || {} : result.output || {});
        setColumnHeaders(headers);
        setFieldMapping(buildDefaultMapping(headers));
        setMappingConfirmed(false);
        setMappedData(null);
        setValidationErrors([]);
        setDefaultValues({});
        setPreviewIndex(0);
        toast.success(`Found ${result.output.length || 1} employee records`);
      } else {
        toast.error('Failed to extract data from file');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error processing file');
    } finally {
      setUploading(false);
      setProcessing(false);
    }
  };

  const handleImport = async () => {
    if (!mappedData || !mappingConfirmed) {
      toast.error('Please confirm field mapping before importing');
      return;
    }

    if (!selectedEmployer) {
      toast.error('Please select an employer');
      return;
    }

    setImportProgress(0);
    const results = { success: 0, failed: 0, errors: [] };

    // Dynamic import of tax calculation utilities
    const { calculateOptimalSection125 } = await import('@/components/utils/dynamicTaxCalculations');

    // Try to fetch invitation template once (auto-create if missing and user has permission)
    let inviteTemplate = null;
    if (sendInvites) {
      try {
        const templates = await api.entities.EmailTemplate.filter({ template_key: 'employee_invitation', is_active: true });
        inviteTemplate = templates?.[0] || null;

        if (!inviteTemplate) {
          try {
            const created = await api.entities.EmailTemplate.create({
              template_key: 'employee_invitation',
              name: 'Employee Welcome & Invitation',
              subject: 'Welcome to Your Benefits Program',
              category: 'enrollment',
              variables: ['employeeName', 'employerName', 'enrollmentUrl', 'enrollmentDeadline', 'hrEmail'],
              body_html: '<p>Hi {{employeeName}},</p><p>You have been added to {{employerName}} benefits. Start enrollment: <a href="{{enrollmentUrl}}">{{enrollmentUrl}}</a></p><p>Deadline: {{enrollmentDeadline}}</p><p>Need help? {{hrEmail}}</p>',
              is_active: true
            });
            inviteTemplate = created;
          } catch (seedErr) {
            console.warn('Auto-create invite template failed (likely permissions):', seedErr.message);
          }
        }
      } catch (err) {
        console.warn('Invite template fetch failed:', err.message);
      }
    }

    for (let i = 0; i < mappedData.length; i++) {
      const row = mappedData[i];
      try {
        // Create employee record
        const employeeData = {
          employer_id: selectedEmployer,
          first_name: row.first_name,
          last_name: row.last_name,
          user_email: row.email,
          phone: row.phone,
          date_of_birth: row.date_of_birth,
          gender: row.gender || 'M',
          tobacco_user: row.tobacco_user || false,
          address: row.address,
          city: row.city,
          state: row.state,
          state_of_residence: row.state_of_residence || row.state,
          zip_code: row.zip_code,
          job_title: row.job_title,
          department: row.department,
          hire_date: row.hire_date,
          annual_salary: row.annual_salary,
          pay_frequency: row.pay_frequency || 'bi_weekly',
          filing_status: row.filing_status || 'single',
          dependents: row.dependents || 0,
          w4_allowances: row.w4_allowances || row.dependents || 0,
          employment_status: row.employment_status || 'full_time',
          flex_allocation: row.flex_allocation,
          post_tax_deduction: row.post_tax_deduction,
          enrollment_token: Math.random().toString(36).substring(2, 15),
          is_active: true,
          profile_confirmed: false,
          enrollment_status: 'not_started'
        };

        const newEmployee = await api.entities.Employee.create(employeeData);

        // Optionally send invite email
        if (sendInvites && newEmployee.user_email) {
          try {
            const appUrl = window.location.origin;
            const employer = employers.find((e) => e.id === selectedEmployer);
            const deadlineDate = new Date();
            deadlineDate.setDate(deadlineDate.getDate() + 30);
            const deadlineStr = deadlineDate.toISOString().slice(0, 10);
            const tokens = {
              employeeName: `${row.first_name || ''} ${row.last_name || ''}`.trim(),
              employerName: employer?.company_name || 'Your Employer',
              enrollmentUrl: appUrl,
              enrollmentDeadline: deadlineStr,
              hrEmail: employer?.hr_contact_email || ''
            };

            let subject = 'You have been added to your benefits portal';
            let html = `<p>Hi ${tokens.employeeName || ''},</p>
              <p>You have been added to the ${tokens.employerName} benefits portal.</p>
              <p>Sign in to begin your enrollment: <a href="${tokens.enrollmentUrl}">${tokens.enrollmentUrl}</a></p>
              <p>Enrollment deadline: ${tokens.enrollmentDeadline}</p>
              <p>If you received an invitation code, use it during sign-in. Otherwise, contact HR for your invite details.</p>`;
            let text = `Hi ${tokens.employeeName || ''}, You have been added to the ${tokens.employerName} benefits portal. Sign in at ${tokens.enrollmentUrl}. Deadline: ${tokens.enrollmentDeadline}.`;

            if (inviteTemplate) {
              subject = fillTemplate(inviteTemplate.subject || subject, tokens) || subject;
              html = fillTemplate(inviteTemplate.body_html || html, tokens) || html;
              text = text;
            }

            await api.email.send(newEmployee.user_email, subject, html, text);

            // Optional drip reminders (send weekly until deadline, capped at 4 sends)
            if (sendReminders) {
              const reminders = [];
              const maxReminders = 4;
              for (let w = 1; w <= maxReminders; w++) {
                const reminderDate = new Date();
                reminderDate.setDate(reminderDate.getDate() + w * 7);
                if (reminderDate > deadlineDate) break;
                reminders.push({
                  to: newEmployee.user_email,
                  subject: `Reminder: Complete your enrollment (Week ${w})`,
                  html: `<p>Hi ${tokens.employeeName || ''},</p>
                    <p>This is your reminder to complete benefits enrollment for ${tokens.employerName}.</p>
                    <p>Deadline: ${tokens.enrollmentDeadline}</p>
                    <p><a href="${tokens.enrollmentUrl}">Go to enrollment</a></p>`,
                  text: `Reminder to complete enrollment for ${tokens.employerName}. Deadline: ${tokens.enrollmentDeadline}. Visit ${tokens.enrollmentUrl}`,
                  send_at: reminderDate.getTime()
                });
              }

              if (reminders.length > 0) {
                try {
                  await apiClient.post('/reminders/schedule', { reminders });
                } catch (remErr) {
                  console.warn('Reminder scheduling failed:', remErr.message);
                }
              }
            }
          } catch (emailErr) {
            console.warn('Invite email failed:', emailErr.message);
          }
        }

        // Calculate optimal flex amount in background (non-blocking)
        try {
          const optimal = await calculateOptimalSection125(newEmployee);
          await api.entities.Employee.update(newEmployee.id, {
            optimal_flex_amount: optimal.optimalAmount,
            calculated_tax_savings: optimal.maxAnnualBenefit
          });
        } catch (taxErr) {
          console.warn('Tax calculation skipped:', taxErr.message);
        }

        results.success++;
      } catch (err) {
        results.failed++;
        results.errors.push(`${row.first_name} ${row.last_name}: ${err.message}`);
      }
      setImportProgress(((i + 1) / mappedData.length) * 100);
    }

    // Log the import
    await api.entities.AuditLog.create({
      entity_type: 'Census',
      entity_id: selectedEmployer,
      action: 'create',
      performed_by: (await api.auth.me()).email,
      notes: `Imported ${results.success} employees from census file with tax calculations`
    });

    setImportResults(results);
    toast.success(`Imported ${results.success} employees with personalized tax analysis`);
  };

  const downloadTemplate = () => {
    const headers = 'first_name,last_name,email,phone,date_of_birth,gender,tobacco_user,address,city,state,state_of_residence,zip_code,job_title,department,hire_date,annual_salary,pay_frequency,filing_status,dependents,w4_allowances,employment_status';
    const sample = 'John,Doe,john.doe@example.com,(555) 123-4567,1985-06-15,M,false,123 Main St,New York,NY,NY,10001,Software Engineer,Engineering,2022-01-15,85000,bi_weekly,married,2,2,full_time';
    const csv = `${headers}\n${sample}`;
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'census_template.csv';
    a.click();
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Census Upload</h1>
        <p className="text-slate-500 mt-1">
          Import employees from a census file (CSV, Excel)
        </p>
      </div>

      {/* Step 1: Select Employer */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-emerald-500" />
            Step 1: Select Employer
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={selectedEmployer} onValueChange={setSelectedEmployer}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select an employer..." />
            </SelectTrigger>
            <SelectContent>
              {employers.map(employer => (
                <SelectItem key={employer.id} value={employer.id}>
                  {employer.company_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Step 2: Upload File */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-blue-500" />
            Step 2: Upload Census File
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-emerald-400 transition-colors">
            <Input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileChange}
              className="hidden"
              id="file-upload"
            />
            <label htmlFor="file-upload" className="cursor-pointer">
              <FileSpreadsheet className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600 font-medium">
                {file ? file.name : 'Click to upload or drag and drop'}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                CSV, Excel files supported
              </p>
            </label>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={downloadTemplate}>
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
            
            <Button 
              onClick={handleUploadAndExtract}
              disabled={!file || !selectedEmployer || uploading || processing}
              className="flex-1 bg-emerald-500 hover:bg-emerald-600"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Process File
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Step 3: Map & Validate */}
      <AnimatePresence>
        {extractedData && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-500" />
                  Step 3: Map & Validate
                  <Badge className="ml-2 bg-emerald-100 text-emerald-700">
                    {extractedData.length} Records
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Mapping table */}
                <div className="border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Source Column</TableHead>
                        <TableHead>Map To</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {columnHeaders.map((col) => (
                        <TableRow key={col}>
                          <TableCell className="font-medium">{col}</TableCell>
                          <TableCell>
                            <Select
                              value={fieldMapping[col] || '_skip_'}
                              onValueChange={(value) => {
                                const next = { ...fieldMapping, [col]: value === '_skip_' ? '' : value };
                                setFieldMapping(next);
                                setMappingConfirmed(false);
                                setMappedData(null);
                              }}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select field or skip" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_skip_">Skip this column</SelectItem>
                                {TARGET_FIELDS.map((field) => (
                                  <SelectItem key={field} value={field}>
                                    {field}
                                    {REQUIRED_FIELDS.includes(field) ? ' (required)' : ''}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Defaults for unmapped fields */}
                <div className="border rounded-lg p-4 space-y-3 bg-slate-50">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <p className="font-medium text-slate-800">Defaults</p>
                      <p className="text-sm text-slate-600">Use defaults to fill missing values for any field.</p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Select value={defaultField} onValueChange={setDefaultField}>
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="Select field" />
                        </SelectTrigger>
                        <SelectContent>
                          {TARGET_FIELDS.map((f) => (
                            <SelectItem key={f} value={f}>{f}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        className="w-48"
                        placeholder="Default value"
                        value={defaultValueInput}
                        onChange={(e) => setDefaultValueInput(e.target.value)}
                      />
                      <Button
                        onClick={() => {
                          if (!defaultField) return;
                          setDefaultValues((prev) => ({ ...prev, [defaultField]: defaultValueInput }));
                          setMappingConfirmed(false);
                          setMappedData(null);
                          setValidationErrors([]);
                          toast.success(`Default set for ${defaultField}`);
                        }}
                        disabled={!defaultField}
                      >
                        Save Default
                      </Button>
                    </div>
                  </div>
                  {Object.keys(defaultValues).length > 0 && (
                    <div className="text-sm text-slate-700 flex flex-wrap gap-2">
                      {Object.entries(defaultValues).map(([k, v]) => (
                        <span key={k} className="px-2 py-1 bg-white border rounded">
                          {k}: {v}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="space-y-1 text-sm text-slate-600">
                    <p className="font-medium text-slate-800">Required fields: first_name, last_name, email</p>
                    {validationErrors.length > 0 && (
                      <p className="text-amber-700">{validationErrors[0]}{validationErrors.length > 1 ? ` (+${validationErrors.length - 1} more)` : ''}</p>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setFieldMapping(buildDefaultMapping(columnHeaders));
                        setMappingConfirmed(false);
                        setMappedData(null);
                        setValidationErrors([]);
                      }}
                    >
                      Reset Mapping
                    </Button>
                    <Button
                      onClick={() => {
                        const applied = applyMapping(extractedData, fieldMapping, defaultValues);
                        const errors = validateRows(applied);
                        setMappedData(applied);
                        setValidationErrors(errors);
                        setMappingConfirmed(true);
                        setPreviewIndex(0);
                        if (errors.length > 0) {
                          toast.error('Validation issues found. Please review.');
                        } else {
                          toast.success('Mapping confirmed and data validated');
                        }
                      }}
                      className="bg-emerald-500 hover:bg-emerald-600"
                    >
                      Confirm Mapping
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Step 4: Review & Import */}
      <AnimatePresence>
        {mappedData && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-500" />
                  Step 4: Review & Import
                  <Badge className="ml-2 bg-emerald-100 text-emerald-700">
                    {mappedData.length} Records
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Validation summary */}
                {validationErrors.length > 0 && (
                  <div className="p-3 rounded-lg bg-amber-50 text-amber-800 text-sm">
                    {validationErrors.slice(0, 3).map((err, idx) => (
                      <div key={idx}>{err}</div>
                    ))}
                    {validationErrors.length > 3 && (
                      <div>+{validationErrors.length - 3} more</div>
                    )}
                  </div>
                )}

                {/* Record preview with navigation */}
                <div className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="font-medium text-slate-800">Preview record {safePreviewIndex + 1} of {mappedData.length}</div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={safePreviewIndex <= 0}
                        onClick={() => setPreviewIndex((idx) => Math.max(idx - 1, 0))}
                      >
                        Prev
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={safePreviewIndex >= mappedData.length - 1}
                        onClick={() => setPreviewIndex((idx) => Math.min(idx + 1, mappedData.length - 1))}
                      >
                        Next
                      </Button>
                    </div>
                  </div>

                  {currentPreview ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                      {Object.entries(currentPreview).map(([key, value]) => (
                        <div key={key} className="flex justify-between gap-3 border rounded px-3 py-2 bg-slate-50">
                          <span className="font-medium text-slate-700">{key}</span>
                          <span className="text-slate-800 break-all">{value ?? '-'}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-600">No preview available</p>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <input
                    id="send-invites"
                    type="checkbox"
                    checked={sendInvites}
                    onChange={(e) => setSendInvites(e.target.checked)}
                  />
                  <label htmlFor="send-invites" className="text-sm text-slate-700">
                    Send invite emails after import
                  </label>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    id="send-reminders"
                    type="checkbox"
                    checked={sendReminders}
                    onChange={(e) => setSendReminders(e.target.checked)}
                  />
                  <label htmlFor="send-reminders" className="text-sm text-slate-700">
                    Send weekly reminders until the deadline (default 30 days)
                  </label>
                </div>

                {/* Import Progress */}
                {importProgress > 0 && importProgress < 100 && (
                  <div className="space-y-2">
                    <Progress value={importProgress} />
                    <p className="text-sm text-center text-slate-600">
                      Importing... {Math.round(importProgress)}%
                    </p>
                  </div>
                )}

                {/* Import Results */}
                {importResults && (
                  <div className={`p-4 rounded-lg ${importResults.failed > 0 ? 'bg-amber-50' : 'bg-emerald-50'}`}>
                    <div className="flex items-center gap-3">
                      {importResults.failed > 0 ? (
                        <AlertCircle className="w-6 h-6 text-amber-600" />
                      ) : (
                        <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                      )}
                      <div>
                        <p className="font-medium">
                          Successfully imported {importResults.success} employees
                          {importResults.failed > 0 && `, ${importResults.failed} failed`}
                        </p>
                        {importResults.errors.length > 0 && (
                          <p className="text-sm text-amber-700 mt-1">
                            Errors: {importResults.errors.slice(0, 3).join(', ')}
                            {importResults.errors.length > 3 && '...'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {!importResults && (
                  <Button 
                    onClick={handleImport}
                    disabled={validationErrors.length > 0}
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Import {mappedData.length} Employees
                  </Button>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}